import React from 'react';
import BoltIcon from '@mui/icons-material/Bolt';
import { Skeleton } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import type { EntitlementUsageCardProps } from '../types';
import {
  calculateUsagePercentage,
  formatAfterTrialText,
  formatDepletionMessage,
  formatRemainingText,
  formatResetDate,
  formatTrialTitle,
  formatUsageText,
  getDisplayMode,
  getUpgradeMessage,
  isDepleted,
  shouldShowUsage,
} from '../utils/entitlementFormatters';

import {
  AfterTrialText,
  BottomRow,
  CardContainer,
  ContentWrapper,
  DepletionText,
  LeftSide,
  ProgressBarWrapper,
  RemainingText,
  ResetDateText,
  SkeletonWrapper,
  StyledProgressBar,
  TopRow,
  TrialContentWrapper,
  UpgradeLink,
  UsageText,
} from './EntitlementUsageCard.styles';

/**
 * EntitlementUsageCard Component
 *
 * Displays usage and quota information for a user entitlement with:
 * - Usage fraction (e.g., "3/10 Notes created")
 * - Progress bar (always primary purple color)
 * - Remaining quota message
 * - Reset date
 * - Upgrade CTA link
 * - Container query responsive behavior
 *
 * Handles all entitlement scenarios:
 * - Limited quotas with usage tracking
 * - Unlimited quotas
 * - No metering (simple access grants)
 * - Depleted quotas
 * - Various usage periods (daily, weekly, monthly)
 */
export const EntitlementUsageCard: React.FC<EntitlementUsageCardProps> = ({
  entitlement,
  onUpgradeClick,
  showUpgradeLink = true,
  variant = 'standard',
  loading = false,
  noBorderRadius = false,
  source,
  isTrialing = false,
  isNonClinician = false,
  sx,
}) => {
  const { entitlement: entitlementMetrics } = useEventTracking();

  // Loading state
  if (loading) {
    return (
      <CardContainer data-no-border-radius={noBorderRadius} sx={sx}>
        <SkeletonWrapper>
          <Skeleton variant="text"  sx={{ width: "60%", height: 20 }}/>
          <Skeleton variant="rectangular"  sx={{ width: "100%", height: 8 }}/>
          <Skeleton variant="text"  sx={{ width: "40%", height: 16 }}/>
        </SkeletonWrapper>
      </CardContainer>
    );
  }

  // Non-clinician state: show admin-specific message
  if (isNonClinician) {
    return (
      <CardContainer data-variant={variant} data-no-border-radius={noBorderRadius} sx={sx}>
        <ContentWrapper>
          <UsageText>Feature Not Available</UsageText>
          <RemainingText>
            As an admin, you do not have access to this feature. To unlock, please contact your
            practice administrator to upgrade your seat.
          </RemainingText>
        </ContentWrapper>
      </CardContainer>
    );
  }

  // Don't show usage UI for non-metered capabilities (unless trialing)
  if (!entitlement || (!isTrialing && !shouldShowUsage(entitlement))) {
    return null;
  }

  // Calculate display values - these are lightweight string operations
  const displayMode = getDisplayMode(entitlement);
  const usageText = formatUsageText(entitlement);
  const remainingText = formatRemainingText(entitlement);
  const depletionMessage = formatDepletionMessage(entitlement);
  const resetDate = formatResetDate(entitlement);
  const upgradeMessage = getUpgradeMessage(entitlement);
  const usagePercentage = calculateUsagePercentage(entitlement);
  const depleted = isDepleted(entitlement);

  // Track upgrade click with metadata
  const handleUpgradeClick = () => {
    entitlementMetrics.upgradeClicked({
      capability_name: entitlement.capability_name,
      source,
      current_usage: entitlement.current_usage ?? undefined,
      quota: entitlement.quota ?? undefined,
      quota_remaining: entitlement.quota_remaining ?? undefined,
      is_depleted: depleted,
      bundle_name: entitlement.bundle_name ?? undefined,
      reset_date: entitlement.current_period_end ?? undefined,
    });

    onUpgradeClick?.();
  };

  // Trial mode: separate render path with different layout
  if (isTrialing) {
    const trialTitle = formatTrialTitle(entitlement.capability_name);
    const afterTrial = formatAfterTrialText(
      entitlement.capability_name,
      entitlement.usage_period ?? null,
    );

    return (
      <CardContainer data-variant={variant} data-no-border-radius={noBorderRadius} sx={sx}>
        <TrialContentWrapper>
          <UsageText>{trialTitle}</UsageText>
          <ProgressBarWrapper>
            <StyledProgressBar
              variant="determinate"
              value={100}
              aria-label="Unlimited during trial"
            />
          </ProgressBarWrapper>
          <AfterTrialText>
            <strong>{afterTrial.prefix}</strong> {afterTrial.message}
          </AfterTrialText>
        </TrialContentWrapper>
      </CardContainer>
    );
  }

  // Non-trial mode: existing behavior
  const showProgressBar = displayMode === 'limited';

  return (
    <CardContainer data-variant={variant} data-no-border-radius={noBorderRadius} sx={sx}>
      <ContentWrapper>
        {/* Left side: Top row + Progress bar */}
        <LeftSide>
          <TopRow>
            <UsageText>{usageText}</UsageText>
            {depleted && depletionMessage && <DepletionText>{depletionMessage}</DepletionText>}
            {!depleted && remainingText && <RemainingText>{remainingText}</RemainingText>}
          </TopRow>

          {/* Progress bar (for limited quotas) */}
          {showProgressBar && (
            <ProgressBarWrapper>
              <StyledProgressBar
                variant="determinate"
                value={usagePercentage}
                aria-label={`${usagePercentage}% of quota used`}
              />
            </ProgressBarWrapper>
          )}
        </LeftSide>

        {/* Right side (extra-wide) / Bottom row (other breakpoints): Reset date and upgrade link */}
        <BottomRow>
          {resetDate ? (
            <ResetDateText>{resetDate}</ResetDateText>
          ) : (
            <div /> // Empty div for spacing
          )}

          {showUpgradeLink && onUpgradeClick && (
            <UpgradeLink
              onClick={handleUpgradeClick}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleUpgradeClick();
                }
              }}
              role="button"
              tabIndex={0}
            >
              <BoltIcon sx={{ fontSize: '1rem' }} />
              {upgradeMessage}
            </UpgradeLink>
          )}
        </BottomRow>
      </ContentWrapper>
    </CardContainer>
  );
};

EntitlementUsageCard.displayName = 'EntitlementUsageCard';
